/*
 * Global Axios configuration.
 *
 * Responsibilities:
 * - Send cookies with API requests.
 * - Attach the current bearer token.
 * - Resolve the current tenant consistently.
 * - Attach the tenant through X-Tenant.
 *
 * The backend uses InitializeTenancyByRequestData,
 * so X-Tenant must be present on tenant-specific API requests.
 */

export default function ({
  $axios,
  store,
  app,
}) {
  /**
   * Convert a possible tenant value into a safe key.
   */
  const normaliseTenant = (
    value
  ) => {
    if (
      value == null ||
      value === ''
    ) {
      return null
    }

    return String(value)
      .trim()
      .toLowerCase()
  }

  /**
   * Resolve the tenant in this order:
   *
   * 1. Injected tenant configuration from plugins/tenant.js
   * 2. Profile Vuex tenant selection
   * 3. Older auth-store tenant value, for backwards compatibility
   * 4. Hostname subdomain
   * 5. DEFAULT_TENANT environment variable
   * 6. ucc
   */
  const resolveTenant = () => {
    const injectedTenant =
      normaliseTenant(
        app &&
          app.$tenant &&
          app.$tenant.key
      )

    if (injectedTenant) {
      return injectedTenant
    }

    const profileTenant =
      normaliseTenant(
        store &&
          store.state &&
          store.state.profile &&
          store.state.profile
            .tenant_key
      )

    if (profileTenant) {
      return profileTenant
    }

    // Support the older auth-store shape if it still exists
    // in persisted Vuex data from a previous deployment.
    const authTenant =
      normaliseTenant(
        store &&
          store.state &&
          store.state.auth &&
          (
            store.state.auth
              .current_tenant ||
            store.state.auth.tenant
          )
      )

    if (authTenant) {
      return authTenant
    }

    if (
      process.client &&
      typeof window !==
        'undefined'
    ) {
      const hostname =
        normaliseTenant(
          window.location.hostname
        ) || ''

      const subdomain =
        normaliseTenant(
          hostname.split('.')[0]
        )

      /*
       * Ignore hostnames that are infrastructure names
       * rather than actual tenant identifiers.
       *
       * "tool" is deliberately excluded here so that
       * app.$tenant or DEFAULT_TENANT determines which
       * tenant tool.myuse.ie represents.
       */
      const ignoredSubdomains = [
        'www',
        'tool',
        'admin',
        'api',
        'localhost',
      ]

      if (
        subdomain &&
        !ignoredSubdomains.includes(
          subdomain
        )
      ) {
        return subdomain
      }
    }

    return (
      normaliseTenant(
        process.env
          .DEFAULT_TENANT
      ) || 'ucc'
    )
  }

  $axios.onRequest(
    (config) => {
      /*
       * Allow the browser to send cookies with
       * cross-origin API requests.
       */
      config.withCredentials = true

      /*
       * Axios normally supplies headers, but initialise
       * the objects defensively for older Axios versions.
       */
      if (!config.headers) {
        config.headers = {}
      }

      /*
       * Attach the current Passport bearer token.
       */
      const token =
        store &&
        store.state &&
        store.state.auth &&
        store.state.auth.auth_token

      if (token) {
        const bearer =
          `Bearer ${token}`

        config.headers.Authorization =
          bearer

        /*
         * Retain common.Authorization for compatibility
         * with the older Axios header structure.
         */
        if (
          !config.headers.common
        ) {
          config.headers.common = {}
        }

        config.headers.common.Authorization =
          bearer
      }

      /*
       * Do not overwrite an explicit X-Tenant header
       * supplied by a particular request.
       */
      const existingTenantHeader =
        config.headers[
          'X-Tenant'
        ] ||
        config.headers[
          'x-tenant'
        ]

      if (
        !existingTenantHeader
      ) {
        config.headers[
          'X-Tenant'
        ] = resolveTenant()
      }

      return config
    }
  )
}